"use client";

import { cn } from "@/lib/utils";
import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/molecules/Accordion";
import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  expansionClick,
  tabSwitched,
} from "@/customAnalytics/trackingEvents";

interface FAQ {
  id: number;
  question: string;
  question_text_color?: string;
  answer: string;
  answer_text_color?: string;
  priority: number;
}

interface Category {
  id: number;
  name: string;
  text_color?: string;
  background_color?: string;
  order_index: number;
  status: boolean;
  faqs?: FAQ[];
}

const Faqs1 = ({ data }: { data: any }) => {
  const { data: sectionData } = data;
  const ref = useRef<HTMLElement>(null);

  const {
    title = "Frequently Asked Questions",
    title_text_color = "#000000",
    section_background_color = "#F5F5F5",
    categories = [],
  } = sectionData || {};

  const activeCategories = useMemo(() => {
    if (!categories || categories.length === 0) return [];
    return [...categories]
      .filter((cat: Category) => cat.status)
      .sort((a: Category, b: Category) => a.order_index - b.order_index);
  }, [categories]);

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    activeCategories.length > 0 ? activeCategories[0].id : null,
  );

  const [expandedFaqId, setExpandedFaqId] = useState<string | null>(null);

  const filteredFaqs = useMemo(() => {
    if (selectedCategoryId === null) return [];

    const selectedCategory = activeCategories.find(
      (cat) => cat.id === selectedCategoryId,
    );

    if (
      !selectedCategory ||
      !selectedCategory.faqs ||
      selectedCategory.faqs.length === 0
    ) {
      return [];
    }

    return [...selectedCategory.faqs].sort(
      (a: FAQ, b: FAQ) => a.priority - b.priority,
    );
  }, [activeCategories, selectedCategoryId]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "faqs_section");
  //   }
  // }, []);

  useEffect(() => {
    setExpandedFaqId(null);
  }, [selectedCategoryId]);

  const handleCategoryClick = (categoryId: number) => {
    const category = activeCategories.find((cat) => cat.id === categoryId);
    if (category) {
      tabSwitched({
        section: "faqs_section",
        component: "category_tab",
        label: category.name,
      });
      setSelectedCategoryId(categoryId);
    }
  };

  const handleAccordionValueChange = (value: string) => {
    const faqId = value === "" ? null : value;
    const previousFaqId = expandedFaqId;

    if (faqId && faqId !== previousFaqId) {
      const faqIndex = filteredFaqs.findIndex((f) => f.id.toString() === faqId);
      const faq = filteredFaqs[faqIndex];
      const selectedCategory = activeCategories.find(
        (cat) => cat.id === selectedCategoryId,
      );

      if (faq && selectedCategory) {
        expansionClick({
          section: "faqs_section",
          component: "faq_item",
          label: faq.question,
          index: String(faqIndex),
          entity_name: faq.id.toString(),
          parent_entity: selectedCategory.name,
        });
      }
    } else if (!faqId && previousFaqId) {
      const faqIndex = filteredFaqs.findIndex(
        (f) => f.id.toString() === previousFaqId,
      );
      const faq = filteredFaqs[faqIndex];
      const selectedCategory = activeCategories.find(
        (cat) => cat.id === selectedCategoryId,
      );

      if (faq && selectedCategory) {
        expansionClick({
          section: "faqs_section",
          component: "faq_item",
          label: faq.question,
          index: String(faqIndex),
          entity_name: faq.id.toString(),
          parent_entity: selectedCategory.name,
        });
      }
    }

    setExpandedFaqId(faqId);
  };

  return (
    <section
      ref={ref}
      className="w-full"
      style={{ backgroundColor: section_background_color }}
    >
      <div
        className={cn(
          "mx-auto flex w-full max-w-[754px] flex-col items-center justify-center",
          "gap-6 px-5 py-8 sm:py-[42px]",
          "md:max-w-[860px]",
          "lg:max-w-[870px]",
          "xl:max-w-[1100px]",
        )}
      >
        <h2
          className={cn(
            "xs:text-new-body-24-semibold xs:text-center",
            "sm:text-heading-5-semibold ",
            "md:text-heading-4-semibold",
            "lg:text-new-heading-34-semibold",
            "xl:text-new-heading-42-semibold",
            "line-clamp-2",
            "type-h3",
          )}
          style={{ color: title_text_color }}
        >
          {title}
        </h2>
        <div
          className={cn(
            "flex flex-wrap items-center justify-center",
            "xs:gap-[14px] xs:hide-scrollbar w-full overflow-hidden",
            "sm:gap-[14px] max-w-full overflow-hidden",
          )}
        >
          <div
            className={cn(
              "flex items-center xs:hide-scrollbar xs:justify-start xs:gap-[14px] xs:overflow-x-auto",
              "sm:flex-wrap sm:items-center sm:justify-center",
            )}
          >
            {activeCategories.map((category) => (
              <div
                key={category.id}
                className={cn(
                  "cursor-pointer flex items-center justify-center border-secondary-main rounded-[56px]",
                  "xs:text-heading-8-semibold xs:border xs:px-2 xs:py-1",
                  "sm:min-w-24 sm:text-heading-9-semibold sm:border-[0.8px] sm:px-3 sm:py-1",
                  "md:min-w-28 md:text-heading-8-semibold md:border-[0.9px] md:px-3 md:py-[6px]",
                  "lg:text-body-1-semibold",
                  "xl:text-new-body-24-semibold",
                  "type-h6",
                )}
                style={{
                  backgroundColor:
                    selectedCategoryId === category.id
                      ? category.background_color
                      : "transparent",
                  color:
                    selectedCategoryId === category.id
                      ? "#FFFFFF"
                      : "black",
                  borderColor:
                    selectedCategoryId !== category.id
                      ? "black"
                      : "transparent",
                }}
                onClick={() => handleCategoryClick(category.id)}
              >
                {category.name}
              </div>
            ))}
          </div>
        </div>

        <Accordion
          type="single"
          collapsible
          value={expandedFaqId || ""}
          onValueChange={handleAccordionValueChange}
          className={cn(
            "!z-1 w-full rounded-lg border-[0.8px] border-gray-2",
            "xs:bg-gray-1 xs:px-7 xs:py-3",
            "sm:bg-white sm:px-[18px]",
            "md:px-6",
            "lg:py-[10px]",
            "xl:px-8",
          )}
        >
          {filteredFaqs.length > 0 ? (
            filteredFaqs.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={faq.id.toString()}
                className={cn(
                  "border-gray-3 border-b-[1.6px]",
                  "xs:py-4",
                  "sm:p-[18px] sm:pl-0",
                  "md:p-6 md:pl-0",
                  "xl:py-[42px]",
                )}
              >
                <AccordionTrigger
                  iconClassName="mr-0"
                  className={cn(
                    "!text-heading-9-semibold cursor-pointer !font-semibold",
                  )}
                >
                  <span
                    className={cn(
                      "xs:text-body-2-semibold xs:line-clamp-3",
                      "sm:text-heading-9-semibold sm:line-clamp-1 ",
                      "md:text-heading-8-semibold",
                      "lg:text-body-1-semibold",
                      "xl:text-new-body-24-semibold",
                      "type-h6",
                    )}
                    style={{ color: faq.question_text_color || "#000000" }}
                  >
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent
                  className={cn(
                    "flex flex-col gap-[18px]",
                    "xs:pt-[27px]",
                    "md:pt-6",
                  )}
                >
                  <div key={faq.answer}>
                    <p
                      className={cn(
                        "xs:text-body-3-regular",
                        "sm:text-heading-9-regular",
                        "md:text-heading-8-regular",
                        "lg:text-body-1-regular",
                        "xl:text-new-body-24-regular",
                        "type-body-sm",
                      )}
                      style={{ color: faq.answer_text_color || "#666666" }}
                    >
                      {faq.answer}
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))
          ) : (
            <div className="py-8 text-center text-gray-500">
              No FAQs available for this category.
            </div>
          )}
        </Accordion>
      </div>
    </section>
  );
};

export default Faqs1;
